import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  RotateCcw, RotateCw, FlipHorizontal, FlipVertical,
  ZoomIn, ZoomOut, Eraser, Save, X, Loader2, Undo2, Redo2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from '../../components/ui/use-toast';
import { API_BASE_URL } from '../../config/api';
import { handleUnauthorized } from '../../services/api';
import SignatureCanvas, { TARGET_W, TARGET_H } from './SignatureCanvas';

const SignatureEditor: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fileFromState = (location.state as any)?.file as File | undefined;

  interface EditorState {
    image: HTMLImageElement | null;
    zoom: number;
    rotation: number;
    flipH: boolean;
    flipV: boolean;
    offset: { x: number; y: number };
  }

  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const [bgSensitivity, setBgSensitivity] = useState(180);
  const exportCanvasRef = useRef<HTMLCanvasElement>(null);

  // Undo / Redo history
  const historyRef = useRef<EditorState[]>([]);
  const historyIndexRef = useRef(-1);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const pushHistory = useCallback(() => {
    const state: EditorState = { image, zoom, rotation, flipH, flipV, offset };
    // Truncate any redo states ahead of current index
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    historyRef.current.push(state);
    historyIndexRef.current = historyRef.current.length - 1;
    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(false);
  }, [image, zoom, rotation, flipH, flipV, offset]);

  const applyState = (state: EditorState) => {
    setImage(state.image);
    setZoom(state.zoom);
    setRotation(state.rotation);
    setFlipH(state.flipH);
    setFlipV(state.flipV);
    setOffset(state.offset);
  };

  const handleUndo = () => {
    if (historyIndexRef.current <= 0) return;
    historyIndexRef.current -= 1;
    applyState(historyRef.current[historyIndexRef.current]);
    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(true);
  };

  const handleRedo = () => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    historyIndexRef.current += 1;
    applyState(historyRef.current[historyIndexRef.current]);
    setCanUndo(true);
    setCanRedo(historyIndexRef.current < historyRef.current.length - 1);
  };

  // Load image from navigation state
  useEffect(() => {
    if (!fileFromState) {
      navigate('/profile', { replace: true });
      return;
    }
    const url = URL.createObjectURL(fileFromState);
    const img = new Image();
    img.onload = () => {
      setImage(img);
      const scaleX = TARGET_W / img.width;
      const scaleY = TARGET_H / img.height;
      const initialZoom = Math.max(scaleX, scaleY);
      setZoom(initialZoom);
      // Push initial state to history
      const initial: EditorState = { image: img, zoom: initialZoom, rotation: 0, flipH: false, flipV: false, offset: { x: 0, y: 0 } };
      historyRef.current = [initial];
      historyIndexRef.current = 0;
      setCanUndo(false);
      setCanRedo(false);
    };
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [fileFromState, navigate]);

  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.1, 5));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.1, 0.1));
  const handleRotateLeft = () => setRotation((r) => (r - 90) % 360);
  const handleRotateRight = () => setRotation((r) => (r + 90) % 360);
  const handleFlipH = () => setFlipH((f) => !f);
  const handleFlipV = () => setFlipV((f) => !f);

  const removeBackground = useCallback(() => {
    if (!image) return;
    pushHistory(); // save state before destructive action

    const offscreen = document.createElement('canvas');
    offscreen.width = TARGET_W;
    offscreen.height = TARGET_H;
    const ctx = offscreen.getContext('2d');
    if (!ctx) return;

    const cx = TARGET_W / 2 + offset.x;
    const cy = TARGET_H / 2 + offset.y;
    ctx.translate(cx, cy);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(flipH ? -zoom : zoom, flipV ? -zoom : zoom);
    ctx.drawImage(image, -image.width / 2, -image.height / 2);
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    const imageData = ctx.getImageData(0, 0, TARGET_W, TARGET_H);
    const data = imageData.data;

    // Perceived luminance: 0.299*R + 0.587*G + 0.114*B
    for (let i = 0; i < data.length; i += 4) {
      const luminance = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      if (luminance > bgSensitivity) {
        data[i + 3] = 0;
      }
    }

    ctx.putImageData(imageData, 0, 0);

    const processedImg = new Image();
    processedImg.onload = () => {
      setImage(processedImg);
      setZoom(1);
      setRotation(0);
      setFlipH(false);
      setFlipV(false);
      setOffset({ x: 0, y: 0 });
      toast({ title: 'Background removed', description: `Sensitivity: ${bgSensitivity}. Use Undo if too aggressive.` });
    };
    processedImg.src = offscreen.toDataURL('image/png');
  }, [image, zoom, rotation, flipH, flipV, offset, bgSensitivity, pushHistory]);

  const handleSave = async () => {
    if (!image) return;
    setSaving(true);

    try {
      // Render final 400x150 image
      const exportCanvas = exportCanvasRef.current || document.createElement('canvas');
      exportCanvas.width = TARGET_W;
      exportCanvas.height = TARGET_H;
      const ctx = exportCanvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context not available');

      ctx.clearRect(0, 0, TARGET_W, TARGET_H);
      const cx = TARGET_W / 2 + offset.x;
      const cy = TARGET_H / 2 + offset.y;
      ctx.translate(cx, cy);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(flipH ? -zoom : zoom, flipV ? -zoom : zoom);
      ctx.drawImage(image, -image.width / 2, -image.height / 2);

      const blob = await new Promise<Blob | null>((resolve) =>
        exportCanvas.toBlob(resolve, 'image/png')
      );
      if (!blob) throw new Error('Failed to export image');

      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('signature', blob, 'signature.png');

      const res = await fetch(`${API_BASE_URL}/profile/signature`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) {
        if (res.status === 401) { handleUnauthorized(); return; }
        throw new Error(data.message || 'Upload failed');
      }

      toast({ title: 'Signature saved', description: 'Your signature image has been updated.', variant: 'success' });
      navigate('/profile', { state: { signatureUpdated: true } });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to save signature', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const controls = [
    { icon: <RotateCcw className="h-4 w-4" />, label: 'Rotate Left', onClick: handleRotateLeft },
    { icon: <RotateCw className="h-4 w-4" />, label: 'Rotate Right', onClick: handleRotateRight },
    { icon: <FlipHorizontal className="h-4 w-4" />, label: 'Flip H', onClick: handleFlipH },
    { icon: <FlipVertical className="h-4 w-4" />, label: 'Flip V', onClick: handleFlipV },
    { icon: <ZoomIn className="h-4 w-4" />, label: 'Zoom In', onClick: handleZoomIn },
    { icon: <ZoomOut className="h-4 w-4" />, label: 'Zoom Out', onClick: handleZoomOut },
    { icon: <Eraser className="h-4 w-4" />, label: 'Remove BG', onClick: removeBackground },
  ];

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <Card className="border-0 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50 border-b">
          <CardTitle className="text-purple-800">Signature Editor</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Position your signature within the <strong>400 × 150 px</strong> target area. Drag to reposition.
          </p>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          {/* Canvas */}
          <div className="flex justify-center overflow-auto">
            <SignatureCanvas
              image={image}
              zoom={zoom}
              rotation={rotation}
              flipH={flipH}
              flipV={flipV}
              offset={offset}
              onOffsetChange={setOffset}
            />
          </div>

          {/* Zoom display */}
          <p className="text-center text-xs text-muted-foreground">
            Zoom: {Math.round(zoom * 100)}% &nbsp;|&nbsp; Rotation: {rotation}°
          </p>

          {/* Toolbar */}
          <div className="flex flex-wrap justify-center gap-2">
            <Button variant="outline" size="sm" onClick={handleUndo} disabled={!canUndo} title="Undo">
              <Undo2 className="h-4 w-4" />
              <span className="ml-1.5 hidden sm:inline">Undo</span>
            </Button>
            <Button variant="outline" size="sm" onClick={handleRedo} disabled={!canRedo} title="Redo">
              <Redo2 className="h-4 w-4" />
              <span className="ml-1.5 hidden sm:inline">Redo</span>
            </Button>
            {controls.map((c) => (
              <Button key={c.label} variant="outline" size="sm" onClick={c.onClick} title={c.label}>
                {c.icon}
                <span className="ml-1.5 hidden sm:inline">{c.label}</span>
              </Button>
            ))}
          </div>

          {/* Background removal sensitivity slider */}
          <div className="flex items-center gap-3 px-4">
            <label className="text-xs font-medium text-muted-foreground whitespace-nowrap">BG Sensitivity</label>
            <input
              type="range"
              min={80}
              max={240}
              value={bgSensitivity}
              onChange={(e) => setBgSensitivity(Number(e.target.value))}
              className="flex-1 accent-purple-600 h-2"
            />
            <span className="text-xs text-muted-foreground w-8 text-right">{bgSensitivity}</span>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t">
            <Button variant="outline" onClick={() => navigate('/profile')} disabled={saving}>
              <X className="h-4 w-4 mr-1.5" /> Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !image} className="bg-purple-600 hover:bg-purple-700">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
              Save Signature
            </Button>
          </div>

          {/* Hidden export canvas */}
          <canvas ref={exportCanvasRef} className="hidden" />
        </CardContent>
      </Card>
    </div>
  );
};

export default SignatureEditor;
